import moment from 'moment-timezone';
import { isEmpty } from 'lodash';
import humanizeUtil from '@utils/humanizeUtil';
import getGranularity, { GRANULARITY } from '@src/utils/granularity';
import { GranularityMap } from '@components/common/GranularitySelect/utils';
import { isAppModule, isOdpModule } from '@utils/constants';
import { Annotation, getEndTimeOfIntervalAnnotation } from '@src/utils/common';

export const formatTime = (value) => {
  const { humanizedNumber, humanizedUnit } = humanizeUtil(value, 'time', 'ms');
  return `${humanizedNumber}${humanizedUnit}`;
};

export const humanizeHoc = (value, unit) => {
  switch (unit) {
    case 'Kbps': {
      const { humanizedNumber, humanizedUnit } = humanizeUtil(value * 1000, 'bitrate', 'bps');
      return `${humanizedNumber} ${humanizedUnit}`;
    }
    case 'min': {
      const { humanizedNumber, humanizedUnit } = humanizeUtil(value * 60 * 1000, 'time', 'ms');
      return `${humanizedNumber} ${humanizedUnit}`;
    }
    case 'sec': {
      const { humanizedNumber, humanizedUnit } = humanizeUtil(value * 1000, 'time', 'ms');
      return `${humanizedNumber} ${humanizedUnit}`;
    }
    case 'ms': {
      const { humanizedNumber, humanizedUnit } = humanizeUtil(value, 'time', 'ms');
      return `${humanizedNumber} ${humanizedUnit}`;
    }
    case 'fps':
    case '%':
      return `${value}${unit}`;
    case 'unitless':
    case 'ratio':
      return value;
  }
};

export const axisFormatter = (value, unit) => {
  let hzValue = value;

  switch (unit) {
    case 'Kbps':
    case 'sec':
    case 'ms':
    case 'min':
    case 'fps': {
      // middle value
      if (String(value).includes('-')) {
        let [start, end] = value.split('-').map(Number);
        start = humanizeHoc(start, unit);
        end = humanizeHoc(end, unit);
        hzValue = `${start} - ${end}`;
      }
      // last value
      else if (String(value).includes('>=')) {
        let last: any = Number(value.slice(2));
        last = humanizeHoc(last, unit);
        hzValue = `>= ${last}`;
      }
      // last value
      else if (String(value).includes('>')) {
        let last: any = Number(value.slice(1));
        last = humanizeHoc(last, unit);
        hzValue = `> ${last}`;
      }
      // first value
      else {
        let first: any = Number(value);
        first = humanizeHoc(first, unit);
        hzValue = `${first}`;
      }
      break;
    }
    case 'unitless':
    case 'ratio': {
      break;
    }
    // default behaviour
    case '': {
      const { humanizedNumber, humanizedUnit } = humanizeUtil(Number(value), '', unit);
      hzValue = `${humanizedNumber} ${humanizedUnit}`;
      break;
    }
  }

  return hzValue;
};

// generate empty response to help identify gaps
export const generateFillerData = (interval, annotations: Annotation[]) => {
  const { from, granularity: granularityVal } = interval;
  let { to } = interval;

  const dataGranularity =
    granularityVal ||
    getGranularity({
      from,
      to,
      minuteGranularity: true,
      overview: false,
      shortcut: interval.shortcut,
    });
  const { step, unit } = GranularityMap[dataGranularity];

  // datepicker includes last minute for some intervals but excludes them for EoD cases
  // this workaround adds a minute to ensure diag chart plots the last point
  const toObj = moment(to);
  if (toObj.hour() === 23 && toObj.minute() === 59) {
    to = moment(to).add(1, 'minute');
  }

  const diff = moment(to).diff(moment(from), unit);

  const data: Array<[string, number | null]> = [];
  for (let index = 0; index < diff; index = index + step) {
    data.push([moment(from).add(index, unit).toISOString(), null]);
  }
  /** CE-9928: DPI - Latest Datapoint shows Incomplete Data/dip */
  // CE-11846 - do not remove the last data point for hour granularity
  // 30-day granularity: dropping the last bucket deletes a third (or more) of a
  // multi-month chart, and a boundary artifact (datepicker sends `to` as
  // ...:59:59, one second short of the full 30-day window) makes a COMPLETE
  // trailing bucket read as incomplete. Skip the trim for P30D entirely.
  if (data.length > 1 && ![GRANULARITY.PT1H, GRANULARITY.P30D].includes(dataGranularity)) {
    const endTime = getEndTimeOfIntervalAnnotation(annotations, 'intervalAlignment');
    const [lastPoint] = data[data.length - 1];
    if (
      endTime &&
      !moment(lastPoint).isSame(moment(endTime), unit) &&
      moment(lastPoint).add(step, unit).isAfter(moment(endTime))
    ) {
      data.pop();
    }
  }

  return data;
};

// backend auto adjusts query interval on certain scenarios
// considering user interval for plotting could break the alignment on different granularities
// hence using start time from backend and end time as the max of user or response value
export const getPlotInterval = (interval, data) => {
  try {
    if (isEmpty(data)) {
      return interval;
    }

    const queryFrom = data[0].timestamp;
    const queryTo = data[data.length - 1].timestamp;

    return {
      from: queryFrom,
      to: moment(interval.to).isSameOrAfter(queryTo) ? interval.to : queryTo,
      granularity: interval.granularity,
    };
  } catch (err) {
    return interval;
  }
};

// Temp fix as app has a different time format now
const buildAppLookupData = (data) => {
  const dataLookup = {};
  data.forEach((row) => {
    dataLookup[moment(row.timestamp).toISOString()] = row.results;
  });
  return dataLookup;
};

const buildLookupData = (data) => {
  const dataLookup = {};
  data.forEach((row) => {
    dataLookup[row.timestamp] = row.results;
  });
  return dataLookup;
};

export function formatTimeseriesData({
  metrics,
  data = [],
  interval,
  useAppLookupForPrompt = false,
  annotations = [],
}) {
  const formattedData = {};

  metrics.forEach((m) => (formattedData[m] = []));

  const plotInterval = getPlotInterval(interval, data);
  const fillerData = generateFillerData(plotInterval, annotations);
  // response timestamp lookup
  const dataLookup =
    useAppLookupForPrompt || isAppModule || isOdpModule
      ? buildAppLookupData(data)
      : buildLookupData(data);

  // fill gaps and format response
  fillerData.forEach(([time, value]) => {
    const row = time in dataLookup ? dataLookup[time] : {};
    metrics.forEach((m) => {
      formattedData[m].push([time, m in row ? row[m] : value]);
    });
  });

  return formattedData;
}
